import React, { useContext, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import bkstApi from "../../common/api";
import ConfigContext from "../../common/context/ConfigContext";
import { useNavigate } from "react-router";
import RequestSummary from "../component/RequestSummary";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import { getVariantTotal, isAssistedCheckout } from "../../common/util";
import moment from "moment";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LineItem from "../../common/component/LineItem";
import QuoteChat from "../component/QuoteChat";
import BlockingError from "../../common/component/BlockingError";
import ProductVariants from "../../product/component/ProductVariants";
import QuoteLineItemVariants from "../component/QuoteLineItemVariants";
import currency from "currency.js";
import CheckIcon from "@mui/icons-material/Check";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

export default function Quote() {
  const siteConfig = useContext(ConfigContext);
  const navigate = useNavigate();

  const ref = useRef(null);

  const [request, setRequest] = useState("");
  const [quote, setQuote] = useState("");

  const [items, setItems] = useState("");
  /** Effective variant field schema per line index when `item.rules` drives derived fields */
  const [quoteDerivedFields, setQuoteDerivedFields] = useState({});
  const [errors, setErrors] = useState("");

  const { reqId } = useParams();

  const [loading, setLoading] = useState(true);
  const [blockingError, setBlockingError] = useState("");

  const staffAssist = isAssistedCheckout();

  useEffect(() => {
    bkstApi(`/site/${siteConfig.id}/quote/${reqId}`).then((res) => {
      document.title = res.data.request.title;
      setRequest(res.data.request);
      setQuote(res.data.quote);
      setItems(res.data.quote.items);
      setQuoteDerivedFields({});

      setLoading(false);

      ref.current.scrollIntoView();
    });
  }, [reqId]);

  // Validate required variant fields for all items
  const validate = () => {
    let errors = [];
    items.forEach((item, idx) => {
      if (item.variants && item.variant) {
        const hasRules = Array.isArray(item.rules) && item.rules.length > 0;
        if (hasRules && !quoteDerivedFields[idx]) {
          errors.push(`${item.title}: Updating options...`);
          return;
        }
        const schema = hasRules ? quoteDerivedFields[idx] : item.variants;
        Object.entries(schema).forEach(([key, config]) => {
          if (config.required) {
            const val = item.variant[key];
            // For text fields
            if (config.field.type === "text" || config.field.type === "select") {
              if (!val || !val.name || val.name.trim() === "") {
                errors.push(`${item.title}: ${config.name} is required.`);
              }
            } else if (config.field.type === "photo") {
              if (!val || !val.value || val.value.length === 0) {
                errors.push(`${item.title}: At least one photo is required for ${config.name}.`);
              }
            }
          }
        });
      }
    });
    return errors;
  };

  const addToCart = (e) => {
    e.preventDefault();

    const errorList = validate();
    if (errorList.length > 0) {
      setErrors(errorList);
      return;
    } else {
      setErrors([]);
    }
    for (const item of items) {
      if (!item.productId) {
        let basePrice = currency(item.price);
        const txnFees = item.includeFees && quote.factor ? basePrice.multiply(quote.factor) : 0;
        const priceWithFeeAndSurcharge = currency(basePrice)
          .add(txnFees)
          .add(currency(getVariantTotal(item.variant, item.total)));
        item.total = currency(priceWithFeeAndSurcharge).multiply(item.quantity).value;
      }
    }
    let payload = { type: "quote", refId: request.id, items, fmt: "2.0", updTs: quote.updTs };

    bkstApi
      .post(`/site/${siteConfig.id}/v2/cart`, payload)
      .then((res) => {
        navigate(`/cart/${res.data.cartId}`);
      })
      .catch((error) => {
        setBlockingError(error.response.data.msg);
      });
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    let quoteStatus = quote.status;
    let expiration =
      quote.exdate && quote.exdate.includes("T") ? quote.exdate : `${quote.exdate}T23:59`;
    if (quote.status === "active" && moment(expiration).diff(moment(), "minutes") < 0) {
      quoteStatus = "expired";
    } else if (quote.status === "block") {
      quoteStatus = "unavailable";
    }

    const fulfillment = quote?.fulfillment;
    const fulfillmentDateLabel =
      fulfillment?.date && `${moment(fulfillment.date).format("ddd, MMM D, YYYY")}`;

    let chatButton =
      (quote && (
        <QuoteChat
          reqId={request.id}
          baker={quote.baker}
          name={quote.baker.bizName}
          chat={quote.chat}
        />
      )) ||
      "";

    if (request?.status === "review") {
      return (
        <Container maxWidth="md" ref={ref}>
          <Box pt={6} pb={20}>
            <Typography variant="h1" gutterBottom>
              Quote Request
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Thank you for requesting a quote! We'll review the details & send you a quote shortly.
              You'll receive a text message when your quote is ready. We look forward to serving
              you!
            </Typography>
            <Box my={4}>
              <RequestSummary request={request} />
            </Box>
          </Box>
        </Container>
      );
    } else if (request?.status === "new" || request?.status === "pending") {
      return (
        <Container maxWidth="md" ref={ref}>
          <Box pt={6} pb={20}>
            <Typography variant="h1" gutterBottom>
              Quote Request
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Thank you for requesting a quote! We'll review the details & send you a quote shortly.
              You'll receive a text message when your quote is ready. We look forward to serving
              you!
            </Typography>
            <Box my={4}>
              <RequestSummary request={request} />
            </Box>
            {chatButton}
          </Box>
        </Container>
      );
    } else if (quoteStatus === "unavailable") {
      return (
        <Container maxWidth="sm" ref={ref}>
          <Box pt={10} pb={20}>
            <Box textAlign="center">
              <SentimentVeryDissatisfiedIcon sx={{ fontSize: "5rem", color: "#e74c3c" }} />
              <Typography variant="h4" gutterBottom>
                We're Sorry!
              </Typography>
              <Typography variant="body1" color="textSecondary">
                We are unable to provide a quote at this time
              </Typography>
            </Box>
            <Box my={4}>
              <Alert severity="error">
                <AlertTitle>Reason</AlertTitle>
                {quote.note ||
                  `This is because we may have sent you another quote and this is a duplicate. Or, you may have informed us that you're no longer interested in a quote. If you believe this is an error, please message us below.`}
              </Alert>
            </Box>
            <Box my={4}>
              <RequestSummary request={request} />
            </Box>
            {chatButton}
          </Box>
        </Container>
      );
    } else if (quoteStatus === "active" || quoteStatus === "expired") {
      return (
        <Container maxWidth="sm" ref={ref}>
          <Box pt={4} pb={20}>
            <form onSubmit={addToCart}>
              <Box>
                <Box>
                  <Typography variant="h1" gutterBottom>
                    👋 {request.firstName || request.name || ""},{" "}
                    <small>here's your custom quote!</small>
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Please review the details below & let us know if you have any questions. When
                    you're ready to order, simply scroll down & tap the Proceed to Checkout
                    button.{" "}
                  </Typography>
                </Box>
                <Box my={2}>
                  {quote?.adjustments?.discount?.value > 0 && (
                    <Box>
                      <Alert severity="success">
                        A discount of{" "}
                        <u>
                          <b>{currency(quote?.adjustments?.discount?.value || 0).format()}</b>
                        </u>{" "}
                        will be applied during checkout.
                      </Alert>
                    </Box>
                  )}
                  {items.map((item, idx) => {
                    return (
                      <Box key={idx} my={2}>
                        <Paper elevation={0}>
                          <Box p={2}>
                            <LineItem item={item} />
                            {quoteStatus === "active" &&
                              item.variants &&
                              Object.keys(item.variants).length > 0 && (
                                <Box mt={2} p={2} sx={{ border: "1px solid #ECECEC" }}>
                                  <Box mb={4}>
                                    <Typography variant="body1" gutterBottom>
                                      Select your Options
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      Tip: you can change your selection even after you place your
                                      order. Simply let us know a week before your order due date.
                                    </Typography>
                                  </Box>
                                  {Array.isArray(item.rules) && item.rules.length > 0 ? (
                                    <QuoteLineItemVariants
                                      item={item}
                                      basePrice={item.total}
                                      onDerivedFields={(fields) =>
                                        setQuoteDerivedFields((prev) => ({
                                          ...prev,
                                          [idx]: fields,
                                        }))
                                      }
                                      onVariantChange={(value) => {
                                        const o = { ...item, variant: value };
                                        const arr = [...items];
                                        arr[idx] = o;
                                        setItems(arr);
                                      }}
                                    />
                                  ) : (
                                    <ProductVariants
                                      basePrice={item.total}
                                      variant={item.variant}
                                      fields={item.variants}
                                      onChange={(value) => {
                                        let o = { ...item, variant: value };

                                        let arr = [...items];
                                        arr[idx] = o;
                                        setItems(arr);
                                      }}
                                    />
                                  )}
                                </Box>
                              )}
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns:
                      fulfillmentDateLabel && fulfillment?.option?.delivery
                        ? "repeat(2, minmax(0, 1fr))"
                        : "minmax(0, 1fr)",
                  }}
                >
                  {fulfillmentDateLabel && (
                    <Paper elevation={0}>
                      <Box px={2} py={0.75}>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.2,
                            display: "block",
                            mb: 0.25,
                            textTransform: "none",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Event Date
                        </Typography>
                        <Typography
                          component="div"
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: "1.0625rem", sm: "1.125rem" },
                            lineHeight: 1.35,
                            letterSpacing: "-0.01em",
                            wordBreak: "break-word",
                          }}
                        >
                          {fulfillmentDateLabel}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                  {fulfillment?.option?.delivery && (
                    <Alert
                      severity="success"
                      icon={<LocalShippingOutlinedIcon />}
                      sx={{ height: "100%", alignItems: "center" }}
                    >
                      Delivery is available! Choose the delivery option in checkout.
                    </Alert>
                  )}
                </Box>
                <Box my={2}>
                  {Array.isArray(errors) && errors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  {quoteStatus === "expired" ? (
                    <Alert severity="error" sx={{ alignItems: "center" }}>
                      <Typography variant="body2" component="div" gutterBottom>
                        Your quote has expired. If you're interested in booking, please message us
                        using the chat widget to check availability.
                      </Typography>
                    </Alert>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Alert severity="info" sx={{ alignItems: "center" }}>
                        <Typography variant="body2" component="div" gutterBottom>
                          Quote expires{" "}
                          <u>
                            <b>{moment(expiration).fromNow()}</b>
                          </u>
                          . Price is subject to change after quote expires.
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </Box>
              </Box>
              {quoteStatus === "active" && (
                <Box my={2}>
                  <Button fullWidth variant="contained" color="primary" type="submit">
                    PROCEED TO CHECKOUT
                  </Button>
                </Box>
              )}
            </form>
            {!staffAssist && chatButton}
            {blockingError && (
              <BlockingError
                title="Error!"
                cta="Refresh Page"
                onClick={() => window.location.reload()}
              >
                <Box>
                  <Typography>{blockingError}</Typography>
                </Box>
              </BlockingError>
            )}
          </Box>
        </Container>
      );
    }
  }
}
